import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../elementsui/CartContext';
import './CartPage.css';
import debounce from 'lodash/debounce';

const CartPage = () => {
  const { cartItems, removeFromCart, updateQuantity, totalPrice } = useCart();
  const navigate = useNavigate();
  const [removingItemId, setRemovingItemId] = useState(null);
  const [updatingQtyId, setUpdatingQtyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleRemoveRef = useRef();
  const handleQuantityChangeRef = useRef();

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    handleRemoveRef.current = debounce((productId, weight) => {
      const uniqueId = `${productId}-${weight}`;
      setRemovingItemId(uniqueId);
      removeFromCart(productId, weight)
        .catch(() => setError('Failed to remove item. Please try again.'))
        .finally(() => setRemovingItemId(null));
    }, 300);

    handleQuantityChangeRef.current = debounce((productId, weight, change) => {
      const item = cartItems.find(i => i.productId === productId && i.weight === weight);
      if (item) {
        const newQuantity = item.quantity + change;
        const uniqueId = `${productId}-${weight}`;
        setUpdatingQtyId(uniqueId);

        if (newQuantity > 0) {
          updateQuantity(productId, weight, newQuantity)
            .catch(() => setError('Failed to update quantity. Please try again.'))
            .finally(() => setUpdatingQtyId(null));
        } else {
          removeFromCart(productId, weight)
            .catch(() => setError('Failed to remove item. Please try again.'))
            .finally(() => setUpdatingQtyId(null));
        }
      }
    }, 300);
  }, [cartItems, removeFromCart, updateQuantity]);

  const handleProceedToCheckout = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in to proceed.');
      navigate('/login');
      return;
    }
    navigate('/order');
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="cartPage-container container py-4">
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="row g-4">
        <div className="col-md-8">
          <div className="cartPage-items-wrapper shadow p-4 bg-white">
            {cartItems.length === 0 ? (
              <p className="text-center text-muted">Your cart is empty.</p>
            ) : (
              cartItems.map((item) => {
                const uniqueId = `${item.productId}-${item.weight}`;
                const isQtyUpdating = updatingQtyId === uniqueId;
                const isRemoving = removingItemId === uniqueId;

                return (
                  <div
                    key={uniqueId}
                    className="cartPage-item d-flex flex-wrap align-items-center border-bottom py-3"
                  >
                    <img
                      src={item.image || '/placeholder-image.jpg'}
                      alt={item.name}
                      className="cartPage-image me-4 mb-2"
                      onError={(e) => (e.target.src = '/placeholder-image.jpg')}
                    />
                    <div className="cartPage-details flex-grow-1 mb-2">
                      <h5 className="fw-semibold mb-1">{item.name}</h5>
                      <p className="text-muted small mb-0">Weight: {item.weight}</p>
                      <div className="d-flex align-items-center mb-2">
                        <span className="fw-bold">
                          ₹{(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                      <div className="d-flex align-items-center">
                        <button
                          className="btn btn-outline-secondary btn-sm me-2"
                          onClick={() =>
                            handleQuantityChangeRef.current(item.productId, item.weight, -1)
                          }
                          disabled={isQtyUpdating || isRemoving}
                        >
                          -
                        </button>
                        <span>{isQtyUpdating ? 'Updating...' : item.quantity}</span>
                        <button
                          className="btn btn-outline-secondary btn-sm ms-2"
                          onClick={() =>
                            handleQuantityChangeRef.current(item.productId, item.weight, 1)
                          }
                          disabled={isQtyUpdating || isRemoving}
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div className="cartPage-actions">
                      <button
                        className="btn cartPage-btn-remove"
                        onClick={() =>
                          handleRemoveRef.current(item.productId, item.weight)
                        }
                        disabled={isRemoving || isQtyUpdating}
                      >
                        {isRemoving ? 'Removing...' : 'Remove'}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        <div className="col-md-4">
          <div className="cartPage-summary shadow p-4 bg-white">
            <h5 className="fw-semibold mb-4">Price Details</h5>
            <div className="d-flex justify-content-between mb-2">
              <span>Price ({cartItems.length} items)</span>
              <span>₹{totalPrice.toFixed(2)}</span>
            </div>
            <div className="d-flex justify-content-between mb-2">
              <span>Delivery Charges</span>
              <span className="text-success">FREE</span>
            </div>
            <hr />
            <div className="d-flex justify-content-between fw-bold h6">
              <span>Total Amount</span>
              <span>₹{totalPrice.toFixed(2)}</span>
            </div>
            <button
              onClick={handleProceedToCheckout}
              className="btn cartPage-btn-order w-100 mt-3"
            >
              Place Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;